import * as Sentry from "@sentry/vue";
import StateUser from "src/store/user/state";
import MaDB from "src/services/api/ma-db";
import Utils from "src/services/helpers/utils";

class MaObjects {
  objects = {};
  buf = {};
  tid = null;
  refsCache = {};
  jsonCache = {};
  send(path, data, cfg) {
    return MaDB.send(path, data, cfg).then((data) => this.decode(data));
  }
  createRef(ref, id) {
    const newRef = new Ref(ref, id);
    const cRef = this.refsCache[newRef.toString()];
    if (cRef) return cRef;
    this.refsCache[newRef.toString()] = newRef;
    return newRef;
  }
  isRef = (ref) => ref instanceof Ref;
  isObject(obj) {
    if (!obj) {
      return false;
    }
    if (
      obj._id instanceof ObjectId &&
      obj._ns &&
      obj.type instanceof Ref &&
      obj.items
    ) {
      return true;
    }
    return false;
  }
  toRef(x, y, default_value) {
    if (!x) {
      return null;
    }
    if (!y) {
      if (typeof x === "string" && x.indexOf(":") > 0) {
        const d = x.split(":");
        if (d.length !== 2) {
          throw new Error("wrong object: ", x);
        }
        return this.createRef(d[0], d[1]);
      }
      if (x._id && x._ns) {
        return this.createRef(x._ns, x._id);
      }
      if (x.$id && x.$ref) {
        return this.createRef(x.$ref, x.$id);
      }
    } else {
      if (typeof x === "string" && typeof y === "string") {
        return this.createRef(x, { $oid: y });
      }
    }
    if (default_value !== void 0) {
      return default_value;
    }
    throw new Error("wrong object", x);
  }
  // test:000a -> { $ref: "test", $id: { $oid: "000a" } }
  str2ref(ref, delimiter = ":") {
    const tmp = ref.split(delimiter);
    return this.createRef(tmp[0], tmp[1]);
  }
  getCheckesStrRef = (ref) => {
    try {
      this.str2ref(ref);
      return ref;
    } catch (error) {
      Sentry.captureException(error);
      return null;
    }
  };
  // { $ref: "test", $id: { $oid: "000a" } } -> test:000a
  ref2str(ref, delimiter = ":") {
    return typeof ref == "string"
      ? ref
      : ref && ref.$ref + delimiter + ref.$id.$oid;
  }
  obj2str(obj) {
    return this.ref2str(this.toRef(obj));
  }
  decode(v) {
    const one = (v) => {
      if (typeof v === "object") {
        if (v === null) return v;
        // date
        if (v.$date) return new Date(v.$date);
        // ref
        if (v.$ref) return this.createRef(v);
        // objectid
        if (v.$oid) return new ObjectId(v);
        // list
        if (v instanceof Array) return list(v);
        // dict
        return dict(v);
      }
      return v;
    };
    const list = (list) => {
      var r = [];
      for (const v of list) {
        r.push(one(v));
      }
      return r;
    };
    const dict = (obj) => {
      var r = {};
      for (const k of Object.keys(obj)) {
        const v = obj[k];
        r[k] = one(v);
      }
      return r;
    };
    return one(v);
  }
  encode(v) {
    const one = (v) => {
      if (typeof v === "object") {
        if (v === null) return v;
        // date
        if (v instanceof Date) return { $date: v.getTime() };
        // ref
        if (v instanceof Ref) return v;
        // objectid
        if (v instanceof ObjectId) return v;
        // list
        if (v instanceof Array) return list(v);
        // dict
        return dict(v);
      }
      return v;
    };
    const list = (list) => {
      var r = [];
      for (const v of list) {
        r.push(one(v));
      }
      return r;
    };
    function dict(obj) {
      var r = {};
      for (const k of Object.keys(obj)) {
        const v = obj[k];
        r[k] = one(v);
      }
      return r;
    }
    return one(v);
  }
  getObject(ref) {
    ref = this.toRef(ref);
    const add = (ref, promise) => {
      const key = this.ref2str(ref);
      if (!this.buf[key]) {
        this.buf[key] = { ref, promises: [] };
      }
      this.buf[key].promises.push(promise);
      if (!this.tid) {
        this.tid = setTimeout(doQuery, 100);
      }
    };
    const resolve = (promises, object) => {
      for (const promise of promises) {
        promise.resolve(object);
      }
    };
    const reject = (promises, object, ref, d) => {
      var message =
        "Не удалось получить объект из базы данных " +
        (d ? d.name : this.ref2str(ref));
      console.error(message);
      // EventBus.$emit("outErrorMessage", { message, e: [message, ref, d] });
      for (const promise of promises) {
        promise.reject(object);
      }
    };
    const doQuery = () => {
      this.tid = null;
      const abuf = this.buf;
      const refs = [];
      for (const b of Object.values(this.buf)) {
        refs.push(b.ref);
      }
      this.buf = {};
      MaDB.get_objects(refs)
        .then((data) => this.decode(data))
        .then((result) => {
          const received_refs = [];
          for (const r of result) {
            if (!r) continue;
            const ref = this.toRef(r);
            received_refs.push(ref);
            const key = this.ref2str(ref);
            const a = abuf[key];
            if (r._no_access) {
              reject(a.promises, "no_access", a.ref, r);
            } else {
              this.objects[key] = r;
              resolve(a.promises, r);
            }
          }
          if (received_refs.length != refs.length) {
            const difference = refs.filter((x) => !received_refs.includes(x));
            for (const ref of difference) {
              const key = this.ref2str(ref);
              const a = abuf[key];
              reject(a.promises, "not_found", a.ref, null);
            }
          }
        });
    };
    if (ref) {
      const key = this.ref2str(ref);
      if (this.objects[key]) {
        return new Promise((resolve) => resolve(this.objects[key]));
      } else {
        return new Promise((resolve, reject) => {
          add(ref, { resolve, reject });
        });
      }
    } else {
      return new Promise((resolve) => resolve(null));
    }
  }
  getObjects(refs) {
    let ps = [];
    for (const ref of refs) {
      ps.push(this.getObject(ref));
    }
    return Promise.all(ps);
  }
  search({ text, type, backlist, limit, skip, visible }) {
    visible = visible === undefined ? true : visible;
    return MaDB.search(text, type, backlist, limit, skip, visible);
  }
  save(items, ref, type) {
    console.log("MaDB.save items", items);
    items = this.encode(items);
    const is_new = !ref;
    console.log("MaDB.save");
    return MaDB.save({ items, ref, type }, is_new).then((obj) => {
      console.log("MaDB.save obj", obj);
      const key = this.ref2str(this.toRef(obj));
      obj = this.decode(obj);
      this.objects[key] = obj;
      return obj;
    });
  }
  remove(ref, is_removed) {
    return MaDB.remove(ref, is_removed).then((data) => {
      let obj = data.obj;
      const key = this.ref2str(this.toRef(obj));
      obj = this.decode(obj);
      this.objects[key] = obj;
      return obj;
    });
  }
  bindPlugin(ref, pref) {
    return MaDB.bind_plugin(ref, pref).then((data) => {
      let obj = data.obj;
      const key = this.ref2str(this.toRef(obj));
      obj = this.decode(obj);
      this.objects[key] = obj;
      return obj;
    });
  }
  extend(object, properties) {
    for (const key in properties) {
      object[key] = properties[key];
    }
    return object;
  }
  json_get(body) {
    const key = `${body.key}_${this.ref2str(body.ref)}`;
    let json = this.jsonCache[key];
    if (!json) {
      json = MaDB.json_get(body);
      this.jsonCache[key] = json;
    }
    return json;
  }
  clone(d, arr, rec = 20) {
    if (typeof d !== "object") {
      return d;
    }
    if (Array.isArray(d)) {
      return Utils.map(
        d,
        (e) => this.clone(e, arr, rec),
        (e, i) => !arr || i < arr
      );
    }
    if (d instanceof Date) {
      return new Date(d.valueOf());
    }
    if (this.isRef(d)) {
      return this.toRef(d);
    }
    if (this.isObject(d)) {
      return new ObjectId(d);
    }
    let r = {};
    for (const k of Object.keys(d)) {
      if (k === "$alite_id") continue;
      if (rec) {
        r[k] = this.clone(d[k], arr, rec - 1);
      }
    }
    return r;
  }
  async checkTreeForAccess(tree) {
    try {
      return await this.checkNodeRecursively(tree);
    } catch (error) {
      Sentry.captureException(error);
      return false;
    }
  }
  async checkNodeRecursively(node) {
    const { children } = node;
    const objectCheckResult = node.ref
      ? await this.checkObjectForAccess(this.toRef(node.ref))
      : true;
    if (objectCheckResult) {
      if (children) {
        const results = await Promise.all(
          children.map((child) => this.checkNodeRecursively(child))
        );
        node.children = results.filter((child) => child);
        return node;
      }
      return node;
    }
  }
  async checkObjectForAccess(ref) {
    const object = await this.getObject(ref);
    const objAccess = object.items.access;
    if (objAccess === undefined || objAccess === null) return true;
    return this.userHasAccess(objAccess);
  }
  userHasAccess(accessRef) {
    return !!this.userAccesses.find((el) => this.db.isRefsEqual(el, accessRef));
  }
  get userAccesses() {
    try {
      return StateUser().acl?.access;
    } catch (error) {
      Sentry.captureException(error);
      return [];
    }
  }
}

function ObjectId(raw) {
  if (typeof raw === "string") {
    this.$oid = raw;
  } else if (raw.$oid) {
    this.$oid = raw.$oid;
  } else if (raw._id.$oid) {
    this.$oid = raw._id.$oid;
  } else throw "wrong ObjectId";
}
ObjectId.prototype.toString = function () {
  return `{${this.$oid}}`;
};

function Ref(ref, id) {
  if (typeof ref === "object" && ref.$ref) {
    this.$ref = ref.$ref;
    this.$id = new ObjectId(ref.$id);
  } else if (typeof ref === "string" && id) {
    this.$ref = ref;
    this.$id = new ObjectId(id);
  } else {
    throw "wrong Ref";
  }
}
Ref.prototype.toString = function () {
  return `{${this.$ref}:${this.$id.$oid}}`;
};
Ref.prototype.str = function () {
  return MaObjects.ref2str(this);
};

export default new MaObjects();
